import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { getErrorMessage } from '../types';
import { useTranslation } from '../i18n';
import { ConnectStatus } from '../components/PayoutSetupSection';
import { SelfBillingAcceptance } from '../components/SelfBillingAcceptance';
import { setFlashMessage } from '../utils/flashMessage';

export const AccountPayoutsReturnPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(true);

  const reloadStatus = async () => {
    const res = await api.get<ConnectStatus>('/connect/status');
    setStatus(res.data);
    return res.data;
  };

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    const delayMs = 1500;

    const poll = async () => {
      while (!cancelled && attempts < maxAttempts) {
        attempts += 1;
        try {
          const data = await reloadStatus();
          if (cancelled) return;
          if (data.detailsSubmitted) {
            setPolling(false);
            return;
          }
        } catch (err) {
          if (cancelled) return;
          setError(getErrorMessage(err, t('connect.status_load_error')));
          setPolling(false);
          return;
        }
        await new Promise(r => setTimeout(r, delayMs));
      }
      if (!cancelled) setPolling(false);
    };

    void poll();
    return () => { cancelled = true; };
  }, [t]);

  const onboardingComplete = Boolean(
    status?.detailsSubmitted && status.chargesEnabled && status.payoutsEnabled
  );

  useEffect(() => {
    if (onboardingComplete && status?.selfBillingAccepted) {
      setFlashMessage(t('self_billing.accepted'));
      navigate('/account?tab=owner', { replace: true });
    }
  }, [onboardingComplete, status?.selfBillingAccepted, navigate, t]);

  const handleReturnBack = () => {
    navigate('/account?tab=owner');
  };

  return (
    <div>
      <h2 className="section-title">{t('connect.return_title')}</h2>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {polling && !error && (
        <p className="text-muted">{t('connect.return_polling')}</p>
      )}

      {status && status.detailsSubmitted && status.chargesEnabled && status.payoutsEnabled && (
        <div className="alert alert-success">{t('connect.return_active')}</div>
      )}

      {status && status.detailsSubmitted && (!status.chargesEnabled || !status.payoutsEnabled) && (
        <div className="alert alert-warning">{t('connect.return_pending_review')}</div>
      )}

      {status && !status.detailsSubmitted && !polling && (
        <div className="alert alert-warning">{t('connect.return_incomplete')}</div>
      )}

      {onboardingComplete && !status?.selfBillingAccepted && (
        <SelfBillingAcceptance onAccepted={() => { void reloadStatus(); }} />
      )}

      {onboardingComplete && status?.selfBillingAccepted && (
        <div className="alert alert-success" style={{ marginTop: '1rem' }}>{t('self_billing.accepted')}</div>
      )}

      <div className="form-actions" style={{ marginTop: '1rem' }}>
        <button type="button" className="btn btn-primary" onClick={handleReturnBack}>
          {t('connect.return_back')}
        </button>
        <Link to="/manage" className="btn btn-secondary">{t('connect.return_to_manage')}</Link>
      </div>
    </div>
  );
};
